/* eslint-disable jsx-a11y/anchor-is-valid */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import tw from 'twin.macro';

import React, { ReactElement, useRef, useState } from 'react';
import { ReactComponent as ArrowDownSvg } from 'assets/Icons/speaker-arrow-down.svg';
import { SimpleMenuSurface } from '@rmwc/menu';
import { useToggle } from 'react-use';
import { Keys } from 'utils/enum';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { Hint } from 'components/shared/Hint';

import { addSpeaker } from 'slices/player.slice';
import { isEmpty, isEqual } from 'lodash';
import { IRichTextInput } from 'components/shared/RichTextEditor/rich-text.model';
import { useReadOnly } from 'slate-react';
import { hoverDropShadow, itemStyles } from 'components/shared/twin.styles';
import { SearchInput } from 'components/shared/SearchInput';
import { toSpeakerRaw } from '../../MediaUtilities';
import { Info } from 'components/shared/Info';
import { SpeakerItem } from './SpeakerItem';
import { QuestionHint } from 'components/shared/QuestionHint';
import { ReactComponent as CloseSVG } from 'assets/Icons/transcript-remove.svg';
import { Switcher } from 'components/shared/Switcher';
import { toggleApplyToAllSpeaker } from 'slices/toggle.slice';

interface IProps {
  node: IRichTextInput;
  showDropdown: boolean;
  toggleShowDropdown: (nextValue: boolean) => void;
  toggleDoubleClickSpeaker: (nextValue: boolean) => void;
}

// Custom comparer to prevent unnecessary re-renders
const comparer = (prev: IProps, next: IProps): boolean => {
  return (
    prev.showDropdown === next.showDropdown &&
    isEqual(prev.node.data, next.node.data)
  );
};

export const SpeakerDropdown = React.memo((props: IProps): ReactElement => {
  // console.count('=== SpeakerDropdown');

  const { showDropdown } = props;

  const dispatch = useDispatch();

  const player = useSelector((state: RootState) => state.player);
  const speakerIdentification = useSelector(
    (state: RootState) => state.speakerIdentification,
  );
  const toggle = useSelector((state: RootState) => state.toggle);

  const [isShowInput, toggleInput] = useToggle(false);
  const [newSpeakerName, setNewSpeaker] = useState('');
  const [searchName, setSearchName] = useState<string>('');

  // RENDER speaker with prompt is ON
  const searchedSpeakers = speakerIdentification.speakers.filter(
    (speaker) =>
      speaker?.hasPrompt &&
      speaker.speakerName.toLowerCase().includes(searchName.toLowerCase()),
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const isReadonly = useReadOnly();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.code === Keys.ESC) {
      resetInput();
    }
    if (e.code === Keys.ENTER || e.code === Keys.ENTER_NUMPAD) {
      const newName = newSpeakerName?.trim();
      if (!isEmpty(newName)) {
        dispatch(addSpeaker(newName));
      }
      resetInput();
    }
  };

  const handleSearchSpeaker = (value: string) => {
    setSearchName(value);
  };

  const resetInput = () => {
    setNewSpeaker('');
    toggleInput(false);
  };

  return (
    <Hint open={false} text="Speaker name">
      <div tw="flex justify-between items-center">
        {!isReadonly && (
          <SimpleMenuSurface
            open={showDropdown}
            onClose={resetInput}
            handle={
              <div tw="flex cursor-pointer hover:filter[drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.3))] pl-4">
                <ArrowDownSvg />
              </div>
            }
            tw="mt-4"
          >
            <div tw="flex min-width[40rem] relative">
              <CloseSVG
                tw="cursor-pointer [g]:(fill[#7f8090]) absolute right[6px] top[8px]"
                color={'#7f7f7f'}
                css={hoverDropShadow(0.3)}
                onClick={() => {
                  props.toggleShowDropdown(false);
                }}
              />

              <div tw="(w-1/2 p-4)! flex flex-col justify-between">
                <div>
                  <div>
                    <div tw="flex pl-2 justify-between items-center">
                      <div tw="text-14 text-center font-medium text-sonnant-purple-2">
                        Speaker
                      </div>
                      <QuestionHint
                        notTransparent
                        hideCloseButton
                        type={'information'}
                        align={'bottom'}
                        action={'hover'}
                        text={
                          <div tw="text-15">
                            Select the speaker by either selecting from the
                            Library Speakers, adding a new speaker or double
                            clicking on this speaker to rename
                          </div>
                        }
                        customStyle={customTooltipCss}
                      />
                    </div>
                    {showDropdown &&
                      player.speakers.map((speaker) => (
                        <SpeakerItem
                          tw="-mb-1"
                          key={speaker.id}
                          speaker={speaker}
                          nodeData={props.node}
                          toggleShowDropdown={props.toggleShowDropdown}
                          toggleDoubleClickSpeaker={
                            props.toggleDoubleClickSpeaker
                          }
                        />
                      ))}
                  </div>

                  {isShowInput ? (
                    <div>
                      <input
                        ref={inputRef}
                        tw="(text-2xl height[3rem] pl-3 mb-0)!"
                        type="text"
                        value={newSpeakerName}
                        onChange={(e) => setNewSpeaker(e.target.value)}
                        placeholder="Add new speaker..."
                        onKeyDown={handleKeyDown}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div
                      css={[itemStyles, tw`cursor-pointer`]}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleInput(true);
                      }}
                    >
                      + Add Speaker
                    </div>
                  )}
                </div>
                <div tw="flex justify-between space-x-4 items-center margin-top[6px]">
                  <div tw="flex space-x-4 items-center">
                    <div tw="text-13 font-medium relative top[1px]">
                      Apply to all
                    </div>
                    <Switcher
                      centered
                      isSmall
                      checked={toggle.isApplyToAllSpeaker}
                      onCheckedChange={(checked) => {
                        dispatch(toggleApplyToAllSpeaker(checked));
                      }}
                    />
                  </div>
                  <QuestionHint
                    notTransparent
                    hideCloseButton
                    type={'information'}
                    align={'bottom'}
                    action={'hover'}
                    text={
                      <div tw="text-15">
                        Applying to all will change the speaker for all captions
                        linked to this speaker to the newly selected or entered
                        speaker. If this is turned off, then only this caption
                        or paragraph will have the speaker changed
                      </div>
                    }
                    customStyle={customTooltipCss}
                  />
                </div>
              </div>

              <div tw="border[1px solid] border-color[#f1f1f1] flex-1 my-4 mr-3"></div>

              <div tw="w-1/2 py-4 mr-3">
                <div tw="flex items-center justify-between">
                  <div tw="text-center text-sonnant-purple-2 font-medium mb-1 text-14 cursor-default">
                    Library Speakers
                  </div>
                </div>
                <div css={[customSearchInputStyle]} tw="mb-1">
                  <SearchInput
                    placeholder="Search speaker name"
                    setTerm={handleSearchSpeaker}
                    autoFocus
                    isSearchTenantLogin
                    noShrink={true}
                  />
                </div>
                <div
                  tw="mb-1 overflow-y-auto h-auto max-height[40rem] text-10!"
                  css={customScrollBar}
                >
                  {showDropdown &&
                    toSpeakerRaw(searchedSpeakers).map((speaker) => (
                      <SpeakerItem
                        key={speaker.id}
                        speaker={speaker}
                        nodeData={props.node}
                        toggleShowDropdown={props.toggleShowDropdown}
                        toggleDoubleClickSpeaker={
                          props.toggleDoubleClickSpeaker
                        }
                        isGlobalSpeaker={true}
                      />
                    ))}

                  {isEmpty(searchedSpeakers) && (
                    <Info text="No result found." fontSize="1.3rem" />
                  )}
                </div>
              </div>
            </div>
          </SimpleMenuSurface>
        )}
      </div>
    </Hint>
  );
}, comparer);

const customScrollBar = css`
  &::-webkit-scrollbar {
    width: 0.5rem;
  }
`;

const customSearchInputStyle = css`
  input:not([type='checkbox']) {
    ${tw`(height[3rem] text-14 font-normal font-family[inherit])!`}
  }

  .search-container button {
    ${tw`(height[3rem] width[3rem] padding-right[2px] padding-bottom[2px])!`}
  }

  span {
    ${tw`(line-height[unset])!`}
  }
`;

const customTooltipCss = css`
  ${tw`text-10 text-justify`}

  .notification {
    ${tw`lg-up:width[40rem]! md-down:(width[40rem] break-words)`}
  }

  .notificationWrapper {
    ${tw`margin-top[1rem]!`}
  }

  .notificationWrapper.information::before {
    ${tw`(top[-0.25rem] left[-0.3rem] relative)`}
  }
`;
